/**
 * useGroupImagePicker — cover/avatar selection shared by the create and edit
 * group forms. Owns the object-URL lifecycle so previews never leak.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type GroupImageSlot = 'cover' | 'avatar';

export interface PickedGroupImage {
  file: File;
  previewUrl: string;
}

export interface UseGroupImagePickerResult {
  coverImage: PickedGroupImage | null;
  avatarImage: PickedGroupImage | null;
  setImage: (slot: GroupImageSlot, file: File) => void;
}

export function useGroupImagePicker(): UseGroupImagePickerResult {
  const [coverImage, setCoverImage] = useState<PickedGroupImage | null>(null);
  const [avatarImage, setAvatarImage] = useState<PickedGroupImage | null>(null);
  const previewUrls = useRef<Partial<Record<GroupImageSlot, string>>>({});

  useEffect(
    () => () => {
      Object.values(previewUrls.current).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    },
    [],
  );

  const setImage = useCallback((slot: GroupImageSlot, file: File) => {
    const staleUrl = previewUrls.current[slot];
    if (staleUrl) URL.revokeObjectURL(staleUrl);
    const previewUrl = URL.createObjectURL(file);
    previewUrls.current[slot] = previewUrl;
    const picked = { file, previewUrl };
    if (slot === 'cover') {
      setCoverImage(picked);
    } else {
      setAvatarImage(picked);
    }
  }, []);

  return { coverImage, avatarImage, setImage };
}
